/**
 * Shared date helpers for consumer-matching actors.
 */

/**
 * Convert a `MM/DD/YYYY` birthday string (with optional whitespace around the
 * slashes — e.g. the masked `MM / DD / YYYY` variant produced by
 * `DateStorefrontInput`) to the `YYYY-MM-DD` ISO format expected by the
 * consumer-match endpoints.
 *
 * Returns `null` for any input we cannot confidently parse (empty string,
 * undefined, missing slashes, non-numeric segments, or an implausible year
 * outside the last 120 years). Callers MUST treat a
 * `null` return as "no usable birthday" and avoid the backend roundtrip
 * rather than silently sending garbage — calling the endpoint with an empty
 * dob causes an unrelated `padStart` crash and produces misleading log
 * traces (see incident: phone-submit `Cannot read properties of undefined
 * (reading 'padStart')`).
 */
export function convertBirthdayToISODate(
  dateStr: string | undefined | null,
): string | null {
  if (!dateStr) return null;

  const cleaned = dateStr.replace(/\s/g, "");
  const parts = cleaned.split("/");
  if (parts.length !== 3) return null;

  const [month, day, year] = parts;
  if (!month || !day || !year) return null;
  if (!/^\d+$/.test(month) || !/^\d+$/.test(day) || !/^\d{4}$/.test(year)) {
    return null;
  }

  // Defense-in-depth against implausible years (e.g. a typo like 1299) slipping
  // past the birthday schema. An out-of-range year gets rejected by the EHR
  // with a 400 that surfaces as 502 noise in error tracking, so treat it as
  // "no usable birthday" and let the caller fall through to NO_CONSUMER_MATCHED.
  // Bound mirrors the kiosk DOBInput's 120-year floor (PLA-2632).
  const yearNum = Number(year);
  const currentYear = new Date().getFullYear();
  if (yearNum < currentYear - 120 || yearNum > currentYear) {
    return null;
  }

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}
